import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import Head from 'next/head';

import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'appLanding.components.WistiaEmbed';

declare global {
  interface Window {
    _wq: any;
  }
}

interface IWistiaEmbedProps {
  videoId: string;
  type: string;
}

const WistiaEmbed = (props: IWistiaEmbedProps) => {
  const { videoId, type } = props;
  const [video, setVideo] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoTime, setVideoTime] = useState(0);

  const handleCancelFullscreen = () => {
    video.pause();
    setIsVideoPlaying(false);
  };

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  const handlePauseVideo = () => {
    setIsVideoPlaying(false);
  };

  const handleEndedVideo = () => {
    video.cancelFullscreen();
    video.time(0);
  };

  const handleClickPlayVideo = () => {
    if (type === 'mobile') {
      video.requestFullscreen();
      video.play();
    } else {
      video.play();
    }
  };

  const bindListeners = () => {
    video.bind('play', handlePlayVideo);
    video.bind('pause', handlePauseVideo);
    video.bind('end', handleEndedVideo);
    video.bind('cancelfullscreen', handleCancelFullscreen);
  };

  useEffect(() => {
    const config = {
      id: videoId,
      onReady: (loadedVideo) => {
        setVideo(loadedVideo);
      },
    };

    /* eslint-disable no-underscore-dangle */
    window._wq = window._wq || [];
    window._wq.push(config);

    return () => {
      window._wq.push({ revoke: config });
      /* eslint-enable no-underscore-dangle */
    };
  }, [videoId]);

  useEffect(() => {
    if (video) {
      bindListeners();
    }
  }, [bindListeners, video]);

  useEffect(() => {
    if (video) {
      setVideoTime(video.time());
    }
  }, [isVideoPlaying, video]);

  return (
    <>
      <Head>
        <script
          async
          type="text/javascript"
          src={`https:fast.wistia.com/embed/medias/${videoId}.jsonp`}
        />
        <script
          async
          type="text/javascript"
          src="https://fast.wistia.com/assets/external/E-v1.js"
        />
      </Head>
      <WistiaEmbed.PlayVideoCta data-qa="play_pause_cta" onClick={handleClickPlayVideo}>
        {videoTime ? t(`${TP}.resumeVideo`, 'Resume Video') : t(`${TP}.playVideo`, 'Play Video')}
      </WistiaEmbed.PlayVideoCta>
      <WistiaEmbed.ResponsivePadding
        data-qa="wistia_player"
        isVideoPlaying={isVideoPlaying}
        className="wistia_responsive_padding"
      >
        <WistiaEmbed.ResponsiveWrapper className="wistia_responsive_wrapper">
          <WistiaEmbed.Embed className={`wistia_embed wistia_async_${videoId} videoFoam=true`}>
            <WistiaEmbed.Swatch className="wistia_swatch">
              <WistiaEmbed.Img
                src={`https:fast.wistia.com/embed/medias/${videoId}/swatch`}
                alt=""
                aria-hidden="true"
              />
            </WistiaEmbed.Swatch>
          </WistiaEmbed.Embed>
        </WistiaEmbed.ResponsiveWrapper>
      </WistiaEmbed.ResponsivePadding>
    </>
  );
};

WistiaEmbed.PlayVideoCta = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 275px;
  padding: 16px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  outline: none;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  text-decoration: underline;

  &:focus {
    outline: none;
  }
  > svg {
    margin-right: 8px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    max-width: 352px;
  }
`;

WistiaEmbed.ResponsivePadding = styled.div<{
  isVideoPlaying: boolean;
}>`
  opacity: 0;
  transition: 300ms opacity;
  z-index: -9000;
  position: unset !important;
  padding: 0 !important;

  ${({ isVideoPlaying }) =>
    isVideoPlaying &&
    css`
      @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
        opacity: 1;
        z-index: auto;
      }
    `}
`;

WistiaEmbed.ResponsiveWrapper = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;
WistiaEmbed.Embed = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;
WistiaEmbed.Swatch = styled.div`
  height: 100%;
  left: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  transition: opacity 200ms;
  width: 100%;
`;
WistiaEmbed.Img = styled.img`
  filter: blur(5px);
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

export default WistiaEmbed;
